/**
 * RechargeHistory - 充值记录 Tab
 */
import { useState, useEffect } from 'react';
import { CreditCard, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface TransactionItem {
  id: string;
  type: string;
  amount: number;
  balance: number;
  description: string;
  metadata: any;
  createdAt: string;
}

export default function RechargeHistory() {
  const { isAuthenticated } = useAuth();
  const [records, setRecords] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchRecords = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/user/transactions?page=1&pageSize=50`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) {
          // 只保留充值和体验金记录（收入类型）
          const rechargeRecords = (data.data?.list || []).filter(
            (t: TransactionItem) => t.type === 'recharge' || t.type === 'trial_bonus'
          );
          setRecords(rechargeRecords);
        } else {
          console.error('获取充值记录失败:', data.error);
        }
      } catch (error) {
        console.error('获取充值记录失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="text-center py-20">
        <CreditCard className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">暂无充值记录</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left text-sm font-medium text-slate-500" style={{ padding: '8px' }}>时间</th>
            <th className="text-left text-sm font-medium text-slate-500" style={{ padding: '8px' }}>金额</th>
            <th className="text-left text-sm font-medium text-slate-500" style={{ padding: '8px' }}>类型</th>
            <th className="text-left text-sm font-medium text-slate-500" style={{ padding: '8px' }}>状态</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
              <td style={{ padding: '8px' }} className="text-slate-700">
                {new Date(record.createdAt).toLocaleString()}
              </td>
              <td style={{ padding: '8px' }} className="font-medium text-slate-800">
                ¥{record.amount.toFixed(2)}
              </td>
              <td style={{ padding: '8px' }} className="text-slate-600">
                {record.description}
              </td>
              <td style={{ padding: '8px' }}>
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  已完成
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
